import { motion } from "framer-motion";
import { Star, Users, Award, TrendingUp } from "lucide-react";
import { clientLogos } from "@/data/landingData";

const stats = [
  { icon: Users, value: "500+", label: "Enseignes posées" },
  { icon: Star, value: "4.9/5", label: "Avis clients" },
  { icon: Award, value: "10 ans", label: "Garantie décennale" },
  { icon: TrendingUp, value: "24h", label: "Délai de réponse" },
];

export function SocialProofSection({ reviews }) {
  const displayReviews = reviews.length > 0 ? reviews.slice(0, 6) : [
    { author: "Marie D.", rating: 5, text: "Excellente prestation ! L'enseigne a transformé ma vitrine.", business: "Boulangerie Le Pain Doré" },
    { author: "Thomas R.", rating: 5, text: "Équipe très professionnelle, respect des délais et qualité irréprochable.", business: "Restaurant Le Comptoir" },
    { author: "Sophie M.", rating: 5, text: "La simulation m'a permis de visualiser exactement le rendu final. Parfait !", business: "Salon Beauté & Bien-être" },
  ];

  return (
    <>
      {/* Stats */}
      <section className="py-16 sm:py-20 border-y border-[#1a1a1a] bg-[#0a0a0a]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {stats.map((stat, idx) => (
              <motion.div key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center" data-testid={`stat-${idx}`}>
                <stat.icon className="w-6 h-6 text-[#00FF66] mx-auto mb-3" />
                <div className="text-3xl sm:text-4xl font-bold font-['Clash_Display',sans-serif] text-white">{stat.value}</div>
                <div className="text-sm text-zinc-500 mt-1 font-['Outfit',sans-serif]">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client logos */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-[#050505]">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-600 mb-8 font-['Outfit',sans-serif]">ILS NOUS FONT CONFIANCE</p>
          <img src={clientLogos[0].logo}
            alt="Nos clients - Carrefour City, Subway, Renault, Norauto, Lidl, Grand Frais"
            className="w-full max-w-4xl mx-auto h-auto grayscale brightness-[1.8] opacity-50 hover:grayscale-0 hover:brightness-100 hover:opacity-100 transition-all duration-700"
            loading="lazy" data-testid="client-logos" />
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.3em] font-medium text-[#00FF66] mb-4 font-['Outfit',sans-serif]">AVIS CLIENTS</p>
            <h2 className="font-['Clash_Display',sans-serif] text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Ce que disent nos clients
            </h2>
            {reviews.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <Star key={n} className="w-4 h-4 fill-[#00FF66] text-[#00FF66]" />)}</div>
                <span className="text-zinc-400 text-sm">{(reviews.reduce((s,r) => s+r.rating,0)/reviews.length).toFixed(1)}/5 — {reviews.length} avis vérifiés</span>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {displayReviews.map((r, idx) => (
              <motion.div key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-[#121212] border border-[#262626] rounded-2xl p-8 hover:border-[#00FF66]/20 transition-colors"
                data-testid={`review-card-${idx}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#00FF66] text-[#00FF66]" />)}
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">"{r.text}"</p>
                <div>
                  <p className="font-semibold text-white text-sm font-['Outfit',sans-serif]">{r.author || r.name}</p>
                  <p className="text-xs text-[#00FF66]">{r.business}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
